import { AsyncComponent } from '@frontend/components/common';
import { Logger } from '@frontend/logger';
import React, { lazy, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';

import { AdminRoutes } from './admin-routes';
import { ApplicationsRoutes } from './applications/applications-routes';
import LicenseError from './error/license-error';
import Header from './header/header-component';
import MobileHeader from './header/mobile-header.component';
import MobileNavbar from './navigation/mobile-navbar.component';
import Navbar from './navigation/navbar/navbar.component';
import { Profile } from './profile/profile.component';
import { ReleaseNotesRoutes } from './release-notes/release-notes-routes';
import { StandaloneRoutes } from './standalone/standalone-routes';
import Unauthorized from './unauthorized/unauthorized';

const StockOverviewReport = lazy(() => import('./stock/stock-overview-report/stock-overview-report'));
const MovementOverviewReport = lazy(() => import('./movement/movement-overview-report/movement-overview-report'));
const ChangePassword = lazy(() => import('./authentication/change-password/change-password.component'));

export function App() {
    const [mobileNavbarOpen, setMobileNavbarOpen] = React.useState(false);
    const location = useLocation();
    const hideAppChrome =
        location.pathname.startsWith('/standalone/accept-user-invite/') ||
        location.pathname.startsWith('/standalone/accept-account-invite/') ||
        location.pathname.startsWith('/standalone/backup-notification-user');

    //TODO fix properly
    useEffect(() => {
        cleanupStaleResolverKeys();
    }, []);

    //routes
    const standalone = StandaloneRoutes();
    const applicationRoutes = ApplicationsRoutes();
    const AdminRoute = AdminRoutes();
    const releaseNotesRoutes = ReleaseNotesRoutes();

    return (
        <div className="bg-background-default flex h-screen w-screen flex-row overflow-hidden font-['Montserrat']">
            {!hideAppChrome && <Navbar className='hidden md:inline-flex' />}
            {!hideAppChrome && (
                <MobileNavbar
                    open={mobileNavbarOpen}
                    onClose={() => setMobileNavbarOpen(false)}
                />
            )}
            <main
                className='no-scrollbar flex h-screen min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-scroll'
                id='app-content'>
                {!hideAppChrome && <Header className='hidden md:block' />}
                {!hideAppChrome && (
                    <MobileHeader
                        className='md:hidden'
                        onMenuClick={() => setMobileNavbarOpen(true)}
                    />
                )}
                <div className={hideAppChrome ? 'flex-grow' : 'flex-grow p-6'}>
                    <AsyncComponent>
                        <Routes>
                            {standalone}
                            {applicationRoutes}
                            {releaseNotesRoutes}
                            {AdminRoute}
                            {/** *
                             // #region Warehouse
                            * */}
                            <Route path='warehouse'>
                                <Route
                                    path='inventory'
                                    element={
                                        <AsyncComponent>
                                            <StockOverviewReport />
                                        </AsyncComponent>
                                    }
                                />
                                <Route
                                    path='history'
                                    element={
                                        <AsyncComponent>
                                            <MovementOverviewReport />
                                        </AsyncComponent>
                                    }
                                />
                            </Route>
                            {/** *
                            // #region Change Password
                                * */}
                            <Route path='change-password'>
                                <Route
                                    index
                                    element={
                                        <AsyncComponent>
                                            <ChangePassword />
                                        </AsyncComponent>
                                    }
                                />
                            </Route>
                            {/** *
                             // #region Profile
                            * */}
                            <Route path='profile'>
                                <Route
                                    index
                                    element={
                                        <AsyncComponent>
                                            <Profile />
                                        </AsyncComponent>
                                    }
                                />
                            </Route>

                            {/** *
                             // #region fallback and error pages
                            * */}
                            <Route
                                path='missing-license'
                                element={<LicenseError />}
                            />
                            <Route
                                path='unauthorized'
                                element={<Unauthorized />}
                            />
                            {/* TODO make proper home page with navigation options
                            <Route
                                path='/'
                                element={<Navigate to='/standalone/backup-notification-user' />}
                            />
                            <Route TODO uncomment if / is fixed
                                path='/*'
                                element={<Navigate to='/' />}
                            /> */}
                        </Routes>
                    </AsyncComponent>
                </div>
            </main>
        </div>
    );
}
export default App;

function cleanupStaleResolverKeys() {
    try {
        // Clean up stale resolver keys that may have been left behind
        const resolverKeys = ['dcm_res', 'dcm_res_loading'];
        const requestLoadingPattern = /^[^_]+_REQ(_loading)?$/;

        const keys = Object.keys(localStorage);
        const keysToRemove = keys.filter((key) => resolverKeys.includes(key) || requestLoadingPattern.test(key));

        keysToRemove.forEach((key) => {
            localStorage.removeItem(key);
        });

        if (keysToRemove.length > 0) {
            Logger.log(`Cleaned up ${keysToRemove.length} stale resolver keys from localStorage`);
        }
    } catch (error) {
        Logger.error('Error cleaning up stale resolver keys:', error);
    }
}
